import React, { forwardRef, useRef, useState } from 'react';
import { Flex, Checkbox, Tree, Empty } from 'antd';
import memoize from 'lodash/memoize';
import isNil from 'lodash/isNil';
import { CheckOutlined } from '@ant-design/icons';
import SelectInput from '../SelectInput';
import SelectedAll, { computedIsSelectAll } from '../SelectedAll';
import SelectedTagList from '../SelectedTagList';
import SimpleBar from 'simplebar-react';
import Fetch from '@kne/react-fetch';
import 'simplebar-react/dist/simplebar.min.css';
import classnames from 'classnames';
import style from './style.module.scss';

const parseTreeData = memoize((data = [], { parentKey, valueKey, childrenKey }) => {
  const parseTree = output => {
    return output.map(node => {
      const children = data.filter(item => {
        return !isNil(node[valueKey]) && item[parentKey] === node[valueKey];
      });
      return Object.assign({}, node, { [childrenKey]: parseTree(children) });
    });
  };

  return {
    treeData: parseTree(data.filter(item => !item[parentKey])),
    ids: data.map(item => item.id)
  };
});

const SelectTree = forwardRef(({ children, ...p }, ref) => {
  const fetchListRef = useRef(null);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const props = Object.assign(
    {},
    {
      parentKey: 'parentId',
      childrenKey: 'children',
      renderItemContent: ({ item, props }) => {
        const { labelKey } = props;
        return (
          <>
            <div className={'select-tree-item-label'}>{item[labelKey]}</div>
          </>
        );
      },
      renderItem: contextProps => {
        const { item, props, isSelectedAll, value, onSelect, setValue, onOpenChange } = contextProps;
        const { single, isPopup, renderItemContent, valueKey } = props;
        const isChecked = value.some(target => target[valueKey] === item[valueKey]);
        return (
          <Flex
            className={classnames(style['default-list-item'], 'select-tree-item', {
              [style['is-selected']]: isChecked,
              [style['is-disabled']]: item.disabled
            })}
            key={item[valueKey]}
            onClick={() => {
              if (item.disabled) {
                return;
              }
              if (isSelectedAll) {
                return;
              }
              if (single) {
                setValue([item]);
              } else {
                onSelect(item);
              }
              if (isPopup && single) {
                onOpenChange(false);
              }
            }}
          >
            {!single && (
              <Flex>
                <Checkbox checked={isSelectedAll || isChecked} disabled={isSelectedAll || item.disabled} />
              </Flex>
            )}
            <Flex vertical gag={8} flex={1} className={'select-tree-item-content'}>
              {renderItemContent(contextProps)}
            </Flex>
            {single && <div className={style['single-checked']}>{isChecked && <CheckOutlined />}</div>}
          </Flex>
        );
      },
      renderTree: contextProps => {
        const { props, isSelectedAll, data } = contextProps;
        const { renderItem, valueKey, labelKey, childrenKey, parentKey } = props;
        const { treeData, ids } = parseTreeData(data, { valueKey, parentKey, childrenKey });
        if (!(treeData && treeData.length > 0)) {
          return props.empty || <Empty />;
        }
        return (
          <Tree
            className={classnames(style['default-list'], {
              [style['is-selected-all']]: isSelectedAll
            })}
            showIcon
            selectable={false}
            expandedKeys={expandedKeys}
            fieldNames={{ title: labelKey, key: valueKey, children: childrenKey }}
            treeData={treeData}
            onExpand={expandedKeys => {
              setExpandedKeys(expandedKeys);
            }}
            titleRender={node => {
              return renderItem(Object.assign({}, contextProps, { item: node }));
            }}
          />
        );
      }
    },
    p
  );
  return (
    <SelectInput {...props} ref={ref}>
      {targetProps => {
        const { props, value, onOpenChange, isMobile, open } = targetProps;
        const { footer, isPopup, valueKey, single, allowSelectedAll, showSelectedTag, api, options, renderTree, selectedAllValue } = props;
        const components = {
          selectedAll: !single && allowSelectedAll && (
            <div
              className={classnames(style['selected-all'], 'select-tree-selected-all', {
                'is-popup': isPopup
              })}
            >
              <SelectedAll />
            </div>
          ),
          treeList: open ? (
            <Fetch
              {...Object.assign(
                {},
                options
                  ? {
                      data: { options },
                      loader: ({ data }) => data.options
                    }
                  : api
              )}
              ref={fetchListRef}
              render={({ data, ...fetchProps }) => {
                const isSelectedAll = computedIsSelectAll(value, selectedAllValue, valueKey);
                const treeContent = renderTree(Object.assign({}, fetchProps, targetProps, { isSelectedAll, data }));
                const scrollClassName = classnames(style['list'], 'select-tree-scroll-list', {
                  'is-popup': isPopup
                });
                // 移动端弹窗内不用 SimpleBar，改原生滚动
                if (isMobile) {
                  return <div className={scrollClassName}>{treeContent}</div>;
                }
                return (
                  <SimpleBar className={scrollClassName} autoHide={false}>
                    {treeContent}
                  </SimpleBar>
                );
              }}
            />
          ) : null,
          selectedTag: showSelectedTag && (
            <div
              className={classnames(style['selected-tag'], 'select-tree-selected-tag', {
                'is-popup': isPopup
              })}
            >
              <SelectedTagList />
            </div>
          ),
          footer: footer && (
            <div className={classnames(style['footer'], 'select-list-footer')}>
              {typeof footer === 'function'
                ? footer({
                    reload: () => {
                      fetchListRef.current && fetchListRef.current.reload();
                    },
                    close: () => {
                      onOpenChange(false);
                    }
                  })
                : footer}
            </div>
          )
        };
        if (typeof children === 'function') {
          return children(Object.assign({}, targetProps, { components }));
        }
        return (
          <Flex vertical className={classnames({ [style['is-mobile']]: isMobile })}>
            {components.search}
            {components.selectedAll}
            {components.treeList}
            {components.footer}
            {components.selectedTag}
          </Flex>
        );
      }}
    </SelectInput>
  );
});

export default SelectTree;
